import { useState, useRef, useCallback, useEffect } from 'react'
import { resetZoom as utilResetZoom, adjustTranslateBounds as utilAdjustTranslateBounds, handleWheelZoom as utilHandleWheelZoom, handlePointerDown as utilHandlePointerDown, handlePointerMove as utilHandlePointerMove, handlePointerUp as utilHandlePointerUp, getTransformStyle as utilGetTransformStyle } from './zoomPanUtils'

/**
 * Custom hook for image zoom and pan in attachment viewer.
 * @param {string} viewerUrl - Current image URL (zoom resets when this changes)
 * @returns {object} Refs, handlers, and getTransformStyle for the zoomed image
 */
export const useImageZoom = (viewerUrl) => {
  const zoomContainerRef = useRef(null)
  const imgRef = useRef(null)
  const scaleRef = useRef(1)
  const translateRef = useRef({ x: 0, y: 0 })
  const isPanningRef = useRef(false)
  const lastPointerRef = useRef({ x: 0, y: 0 })
  const [, forceRerender] = useState(0)
  const [zoomLevel, setZoomLevel] = useState(1)

  const zoomRefs = useRef({
    zoomContainerRef,
    imgRef,
    scaleRef,
    translateRef,
    isPanningRef,
    lastPointerRef
  }).current

  const resetZoom = useCallback(() => {
    utilResetZoom(zoomRefs, forceRerender)
    setZoomLevel(1)
  }, [zoomRefs])

  const adjustTranslateBounds = useCallback(() => {
    utilAdjustTranslateBounds(zoomRefs, forceRerender)
  }, [zoomRefs])

  const handleWheelZoom = useCallback(
    (e) => {
      utilHandleWheelZoom(zoomRefs, forceRerender, adjustTranslateBounds)(e)
      setTimeout(() => setZoomLevel(scaleRef.current), 0)
    },
    [zoomRefs, adjustTranslateBounds]
  )

  const handlePointerDown = useCallback(utilHandlePointerDown(zoomRefs), [zoomRefs])
  const handlePointerMove = useCallback(utilHandlePointerMove(zoomRefs, forceRerender), [zoomRefs])
  const handlePointerUp = useCallback(utilHandlePointerUp(zoomRefs, adjustTranslateBounds), [zoomRefs, adjustTranslateBounds])

  const applyZoom = useCallback(
    (newScale) => {
      const { zoomContainerRef, scaleRef, translateRef } = zoomRefs

      if (!zoomContainerRef.current) return

      const prevScale = scaleRef.current

      if (Math.abs(newScale - prevScale) < 1e-6) return

      const rect = zoomContainerRef.current.getBoundingClientRect()
      const centerX = rect.width / 2
      const centerY = rect.height / 2
      const tx = translateRef.current.x
      const ty = translateRef.current.y
      const coef = (newScale - prevScale) / (prevScale * newScale)
      const nx = tx + coef * (centerX - centerX)
      const ny = ty + coef * (centerY - centerY)

      if (newScale <= 1.00001) {
        scaleRef.current = 1
        translateRef.current = { x: 0, y: 0 }
        setZoomLevel(1)
      } else {
        translateRef.current = { x: nx, y: ny }
        scaleRef.current = newScale
        setZoomLevel(newScale)
      }

      forceRerender((n) => n + 1)
      setTimeout(() => {
        try {
          adjustTranslateBounds()
        } catch (err) {
          console.log('Error adjusting translate bounds after zoom:', err)
        }
      }, 0)
    },
    [zoomRefs, adjustTranslateBounds]
  )

  const handleZoomIn = useCallback(() => {
    const newScale = Math.min(scaleRef.current * 1.2, 5)

    applyZoom(newScale)
  }, [applyZoom])

  const handleZoomOut = useCallback(() => {
    const newScale = Math.max(scaleRef.current * 0.8, 1)

    applyZoom(newScale)
  }, [applyZoom])

  const getTransformStyle = useCallback(() => utilGetTransformStyle(zoomRefs), [zoomRefs])

  useEffect(() => {
    resetZoom()
    setTimeout(() => {
      try {
        adjustTranslateBounds()
      } catch (err) {
        console.log('Error adjusting translate bounds after zoom:', err)
      }
    }, 0)
  }, [viewerUrl, resetZoom, adjustTranslateBounds])

  return {
    zoomContainerRef,
    imgRef,
    zoomRefs,
    handleWheelZoom,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleZoomIn,
    handleZoomOut,
    getTransformStyle,
    zoomLevel
  }
}
